import { AiStatus } from '../entity/api/ai';
import { type AiPrompt } from '../entity/api/aiprompt';
import {
  type AssistantPromptReq,
  type AssistantPromptRes,
  type ContentPromptUpdateSocketResSchema,
  type CreateAiPromptReq,
  type CreateAiPromptRes,
  type ListAiPromptRes,
  type SaveAiPromptReq,
  type SaveAiPromptRes,
  assistantPromptReqSchema,
  contentPromptUpdateSocketResSchema,
  createAiPromptReqSchema,
  deleteAiPromptReqSchema,
  saveAiPromptReqSchema,
} from '../entity/api/routes/ai';
import { SocketEvent } from '../entity/api/routes/socket';
import { BadRequestError, InternalServerError } from '../error';
import { apiRequest } from './request';
import { getSocket } from './socket';

// TODO: remove!!
export function onContentPromptUpdate(data: unknown) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  const res = contentPromptUpdateSocketResSchema.safeParse(parsedData);
  if (!res.success || res.error != null) {
    throw res.error ?? new InternalServerError('unable to parse prompt data');
  }

  return res.data;
}

export async function promptAssistant(data: AssistantPromptReq) {
  const req = assistantPromptReqSchema.parse(data);
  const socket = await getSocket(true);
  if (socket == null) {
    throw new InternalServerError('socket is required');
  }

  return new Promise<ContentPromptUpdateSocketResSchema>(function (resolve, reject) {
    // eslint-disable-next-line @typescript-eslint/require-await
    const listener = async function (data: unknown) {
      try {
        const result = onContentPromptUpdate(data);
        if (result.status === AiStatus.Error || result.status === AiStatus.Cancelled) {
          reject(new BadRequestError(result.error));
          return;
        }

        if (result.status === AiStatus.Success) {
          resolve(result);
          return;
        }
      } catch (err) {
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        reject(err);
      }

      // do nothing in any other case and wait for the next event to come through
      socket.once(SocketEvent.CONTENT_PROMPT_UPDATE, listener);
    };

    // listen for the event that will update the content
    socket.once(SocketEvent.CONTENT_PROMPT_UPDATE, listener);

    // make the request
    apiRequest<AssistantPromptRes>(`/ai/prompt/assistant`, 'POST', true, req.body).catch(
      function (err) {
        reject(err as Error);
      }
    );
  });
}

export async function getAiPromptsList() {
  return apiRequest<ListAiPromptRes>('/ai/prompt/list', 'GET', true);
}

export async function createAiPrompt(data: CreateAiPromptReq['body']) {
  const req = createAiPromptReqSchema.parse({
    params: null,
    query: null,
    body: data,
  });
  return apiRequest<CreateAiPromptRes>('/ai/prompt', 'POST', true, req.body);
}

export async function updateAiPrompt(
  id: SaveAiPromptReq['params']['id'],
  data: SaveAiPromptReq['body']
) {
  const req = saveAiPromptReqSchema.parse({
    params: { id },
    query: null,
    body: data,
  });
  return apiRequest<SaveAiPromptRes>(`/ai/prompt/${req.params.id}`, 'PATCH', true, req.body);
}

export async function removeAiPrompt(id: AiPrompt['_id']) {
  const req = deleteAiPromptReqSchema.parse({
    params: { id },
    query: null,
    body: null,
  });
  return apiRequest<unknown>(`/ai/prompt/${req.params.id}`, 'DELETE', true);
}
